import React from 'react';
import { Trophy, Star, CheckCircle2, Lock } from 'lucide-react';
import { challenges } from '../../data/mockData';
interface ChallengePageProps {
  tenantId: string;
}
export function ChallengePage({
  tenantId
}: ChallengePageProps) {
  const tenantChallenges = challenges.filter(c => c.tenantId === tenantId);
  const completedCount = tenantChallenges.filter(c => c.completed).length;
  const totalCount = tenantChallenges.length;
  const progressPercentage = Math.round(completedCount / totalCount * 100);
  const categories = [{
    id: 'event',
    label: 'Event Challenges',
    color: 'text-purple-600',
    bg: 'bg-purple-100',
    border: 'border-purple-200'
  }, {
    id: 'social',
    label: 'Netzwerk Challenges',
    color: 'text-blue-600',
    bg: 'bg-blue-100',
    border: 'border-blue-200'
  }, {
    id: 'stadium',
    label: 'Stadion Challenges',
    color: 'text-orange-600',
    bg: 'bg-orange-100',
    border: 'border-orange-200'
  }];
  return <div className="space-y-8" data-id="element-413">
      {/* Hero Progress */}
      <div className="bg-slate-900 rounded-2xl p-8 text-white relative overflow-hidden" data-id="element-414">
        <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500 rounded-full blur-3xl opacity-20 -translate-y-1/2 translate-x-1/3" data-id="element-415"></div>

        <div className="relative z-10 flex flex-col md:flex-row items-center gap-8" data-id="element-416">
          <div className="w-32 h-32 rounded-full bg-slate-800 border-4 border-slate-700 flex items-center justify-center relative flex-shrink-0" data-id="element-417">
            <Trophy size={48} className="text-emerald-400" data-id="element-418" />
            <div className="absolute -bottom-2 bg-emerald-500 text-white text-xs font-bold px-3 py-1 rounded-full border-2 border-slate-900" data-id="element-419">
              Level 4
            </div>
          </div>

          <div className="flex-1 text-center md:text-left w-full" data-id="element-420">
            <h1 className="text-3xl font-bold mb-2" data-id="element-421">Ihre Erfolge</h1>
            <p className="text-slate-400 mb-6" data-id="element-422">
              Sammeln Sie Badges durch aktive Teilnahme im Netzwerk.
            </p>

            <div className="space-y-2" data-id="element-423">
              <div className="flex justify-between text-sm font-medium" data-id="element-424">
                <span className="text-emerald-400" data-id="element-425">
                  {completedCount} von {totalCount} Challenges
                </span>
                <span className="text-slate-400" data-id="element-426">{progressPercentage}%</span>
              </div>
              <div className="w-full h-3 bg-slate-800 rounded-full overflow-hidden" data-id="element-427">
                <div className="h-full bg-gradient-to-r from-emerald-500 to-teal-400 rounded-full transition-all duration-1000" style={{
                width: `${progressPercentage}%`
              }} data-id="element-428"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Badges Collection */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6" data-id="element-429">
        <h2 className="text-lg font-bold text-slate-900 mb-6 flex items-center gap-2" data-id="element-430">
          <Star className="text-yellow-500" size={20} data-id="element-431" />
          Ihre Badges
        </h2>
        <div className="flex flex-wrap gap-4" data-id="element-432">
          {tenantChallenges.map(challenge => <div key={`badge-${challenge.id}`} className={`w-20 h-24 flex flex-col items-center justify-center gap-2 rounded-xl border transition-all ${challenge.completed ? 'bg-slate-50 border-slate-200 shadow-sm hover:scale-105' : 'bg-slate-50/50 border-slate-100 opacity-50 grayscale'}`} title={challenge.reward} data-id="element-433">
              <div className="text-3xl relative" data-id="element-434">
                {challenge.badgeEmoji}
                {!challenge.completed && <div className="absolute -bottom-1 -right-1 bg-slate-200 rounded-full p-0.5" data-id="element-435">
                    <Lock size={10} className="text-slate-500" data-id="element-436" />
                  </div>}
              </div>
              <span className="text-[10px] font-bold text-slate-600 text-center leading-tight px-1" data-id="element-437">
                {challenge.reward}
              </span>
            </div>)}
        </div>
      </div>

      {/* Challenges Lists */}
      <div className="space-y-8" data-id="element-438">
        {categories.map(category => {
        const categoryChallenges = tenantChallenges.filter(c => c.type === category.id);
        if (categoryChallenges.length === 0) return null;
        return <div key={category.id} className="space-y-4" data-id="element-439">
              <h3 className="text-lg font-bold text-slate-800" data-id="element-440">
                {category.label}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-441">
                {categoryChallenges.map(challenge => <div key={challenge.id} className={`bg-white rounded-xl border p-5 relative overflow-hidden ${challenge.completed ? 'border-emerald-200 shadow-sm' : 'border-slate-200'}`} data-id="element-442">
                    {challenge.completed && <div className="absolute top-0 right-0 w-12 h-12 bg-emerald-50 flex items-start justify-end p-2 rounded-bl-3xl" data-id="element-443">
                        <CheckCircle2 size={18} className="text-emerald-500" data-id="element-444" />
                      </div>}

                    <div className="flex items-start gap-4 mb-4" data-id="element-445">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-2xl flex-shrink-0 ${challenge.completed ? 'bg-emerald-100' : 'bg-slate-100'}`} data-id="element-446">
                        {challenge.icon}
                      </div>
                      <div className="pr-6" data-id="element-447">
                        <h4 className="font-bold text-slate-900 leading-tight" data-id="element-448">
                          {challenge.title}
                        </h4>
                        <p className="text-xs text-slate-500 mt-1" data-id="element-449">
                          {challenge.description}
                        </p>
                      </div>
                    </div>

                    <div className="space-y-2 mt-auto" data-id="element-450">
                      <div className="flex justify-between text-xs font-medium" data-id="element-451">
                        <span className="text-slate-600" data-id="element-452">Fortschritt</span>
                        <span className={challenge.completed ? 'text-emerald-600' : 'text-slate-600'} data-id="element-453">
                          {challenge.current} / {challenge.target}
                        </span>
                      </div>
                      <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden" data-id="element-454">
                        <div className={`h-full rounded-full transition-all duration-500 ${challenge.completed ? 'bg-emerald-500' : 'bg-slate-400'}`} style={{
                    width: `${challenge.current / challenge.target * 100}%`
                  }} data-id="element-455"></div>
                      </div>
                    </div>
                  </div>)}
              </div>
            </div>;
      })}
      </div>
    </div>;
}